import { useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import { useQueryClient } from "react-query";
import { useLocation } from "react-router-dom";
import { notification } from "antd";
import { PATH } from "../service/client";
import i18n from "../i18n";

export type SocketStatus = "connecting" | "connected" | "disconnected" | "offline";
async function isOnlineReal() {
  try {
    await fetch("https://www.google.com/favicon.ico", {
      mode: "no-cors",
      cache: "no-store",
    });
    return true;
  } catch {
    return false;
  }
}

export const useOrdersSocket = (branchId: number | undefined) => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const socketRef = useRef<Socket | null>(null);
  const locationRef = useRef(location.pathname);
  const [status, setStatus] = useState<SocketStatus>("connecting");

  useEffect(() => {
    locationRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    const updateOnlineStatus = async () => {
      if (await isOnlineReal()) {
        // Połączenie z internetem
        if (status === "offline") {
          setStatus("connecting");  // Możesz zmienić status, gdy powróci połączenie
        }
      } else {
        // Brak połączenia z internetem
        setStatus("offline");
      }
    };

    // Nasłuchuj na zmiany statusu połączenia internetowego
    window.addEventListener("online", updateOnlineStatus);
    window.addEventListener("offline", updateOnlineStatus);

    updateOnlineStatus();  // Ustawa status na początek

    return () => {
      window.removeEventListener("online", updateOnlineStatus);
      window.removeEventListener("offline", updateOnlineStatus);
    };
  }, [status]);



  useEffect(() => {
    if (!branchId) return;

    // test()

    const socket = io(`${PATH}/admin`, {
      transports: ["websocket", "polling"],
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      console.info("[Socket.IO Admin] Polaczono:", socket.id);
      setStatus("connected");
      socket.emit("join_branch", { branchId });
    });

    socket.on("disconnect", (reason) => {
      console.info("[Socket.IO Admin] Rozlaczono:", reason);
      setStatus("disconnected");
    });

    socket.io.on("reconnect_attempt", async () => {
      if (await isOnlineReal()) {
        setStatus("connecting");  // Możesz zmienić status, gdy powróci połączenie
      } else {
        setStatus("offline");  // Możesz zmienić status, gdy powróci połączenie
      }
    });

    socket.on("orders_updated", (data: { isNew?: boolean }) => {
      queryClient.invalidateQueries(["/product-orders"]);

      if (data?.isNew && !locationRef.current.startsWith("/orders")) {
        notification.info({
          message: i18n.t("ordersSocket.newOrderTitle"),
          description: i18n.t("ordersSocket.newOrderDescription"),
          placement: "bottomRight",
          duration: 5,
        });
      }
    });

    socket.on("service_orders_updated", (data: { isNew?: boolean }) => {
      queryClient.invalidateQueries(["/service-orders"]);

      if (data?.isNew && !locationRef.current.startsWith("/reservations")) {
        notification.info({
          message: i18n.t("ordersSocket.newReservationTitle"),
          description: i18n.t("ordersSocket.newReservationDescription"),
          placement: "bottomRight",
          duration: 5,
        });
      }
    });

    return () => {
      socket.emit("leave_branch", { branchId });
      socket.disconnect();
      socketRef.current = null;
    };
  }, [branchId, queryClient]);

  return status;
};
